import React, { useState } from 'react'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types/index'
import { PageHeader } from '@/components/ui/PageHeader'
import { Navigate } from 'react-router-dom'
import AccessSettings from './AccessSettings'
import NavigationSettings from './NavigationSettings'
import NotificationSettings from './NotificationSettings'
import DataSettings from './DataSettings'
import AppSettings from './AppSettings'
// Bug #272: environment-aware badge — hide in production, show
// "Preprod" everywhere else.
import { resolveAppEnvironment } from '@/utils/environment'

type SettingsTab = 'access' | 'navigation' | 'notifications' | 'data' | 'apps' | 'platform'

const Settings: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<SettingsTab>('access')

  const isAdmin = user?.roles?.includes(UserRole.PlatformAdmin)

  // Bug #272: env badge / Platform info field were both hardcoded to
  // "Preprod" — they now derive from hostname + VITE_APP_ENV.
  const appEnvironment = resolveAppEnvironment()
  const isProductionEnv = appEnvironment === 'production'

  if (!isAdmin) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader title="Settings" />
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '8px',
            backgroundColor: 'var(--color-white)',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', marginBottom: '8px' }}>
            Access denied
          </div>
          <p style={{ fontSize: '14px', color: 'var(--color-dark-grey)', margin: 0 }}>
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    )
  }

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'access', label: 'Access' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'data', label: 'Data' },
    { id: 'apps', label: 'Apps' },
    { id: 'platform', label: 'Platform info' },
  ]

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="Settings"
        actions={
          // Bug #272: only render the env badge in non-production
          // environments. Hiding the badge entirely in prod (rather
          // than swapping it to a "Production" label) keeps the
          // visual quiet for the day-to-day case and reserves the
          // warning-coloured chip for the "you're operating in a
          // staging env, double-check what you change" signal.
          isProductionEnv ? null : (
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#b45309',
              }}
            >
              Preprod
            </span>
          )
        }
      />

      <div style={{ display: 'flex', minHeight: 'calc(100vh - 120px)' }}>
        {/* Left sidebar navigation */}
        <div
          style={{
            width: '200px',
            borderRight: '1px solid var(--color-light-grey)',
            padding: '24px 0',
            backgroundColor: '#fafafa',
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? 600 : 500,
                color: activeTab === tab.id ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
                borderLeft: activeTab === tab.id ? '3px solid var(--color-magenta)' : '3px solid transparent',
                fontFamily: 'var(--font-family)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Right content area */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
          {activeTab === 'access' && <AccessSettings />}
          {activeTab === 'navigation' && <NavigationSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'data' && <DataSettings />}
          {activeTab === 'apps' && <AppSettings />}
          {activeTab === 'platform' && (
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
                Platform information
              </h2>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ padding: '16px', border: '1px solid var(--color-light-grey)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px', fontWeight: 500 }}>
                    Version
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>1.0.0</div>
                </div>
                <div style={{ padding: '16px', border: '1px solid var(--color-light-grey)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px', fontWeight: 500 }}>
                    Environment
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
                    {isProductionEnv ? 'Production' : 'Preprod'}
                    {!isProductionEnv && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', padding: '2px 8px', backgroundColor: '#fef3c7', color: '#b45309', borderRadius: '4px' }}>
                        Non-production
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Settings
